import { router } from "@inertiajs/react";
import { format } from "date-fns";
import { LoaderIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useDateContext } from "@/components/providers/date-provider";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getCsrfToken } from "@/lib/csrf";
import {
	NOTES_MAX,
	STOP_FORM_ERROR_FIELD,
	STOP_REASONS,
	type StopForm,
	type StopReason,
	stopFormError,
} from "@/lib/orders/stop-package";
import { cn, formatCurrency } from "@/lib/utils";
import { LedgerLabel } from "./feature-form";

// Long packages would push the bill field off screen; the rest sit behind "show more".
const VISITS_SHOWN = 3;

export interface StopPackageTarget {
	orderId: string;
	// The order's current total_amount: the backend refuses a bill above it.
	totalAmount: number;
	// The backend's suggestion ignores it, so the admin is told when there is one.
	discountAmount: number;
	voucherCode: string | null;
	// packages.price_per_visit: the same base the backend's suggestion multiplies.
	pricePerVisit: number | null;
	packageName: string | null;
	// Who and where, the same four facts the Change Package dialog opens with.
	registrationNumber: string;
	patientName: string | null;
	serviceName: string | null;
	city: string | null;
}

// The backend's StopOrderResponse, camelCased by the controller.
interface StopPreview {
	packageVisits: number;
	completedVisits: number;
	visitsToCancel: {
		appointmentId: string;
		visitNumber: number;
		appointmentDateTime: string | null;
	}[];
	originalTotalAmount: number;
	suggestedBilledAmount: number;
	billedAmount: number;
	settledAmount: number;
	refundAmount: number;
	paymentStatusAfter: string;
}

const payloadOf = (form: StopForm, probeReason?: StopReason) => ({
	stop_reason: form.reason || probeReason,
	notes: form.notes.trim(),
	billed_amount: form.billed.trim(),
});

// What a preview was computed for. Confirm is only offered while it matches the form.
const keyOf = (form: StopForm) => JSON.stringify(payloadOf(form));

const EMPTY: StopForm = { reason: "", notes: "", billed: "" };

export function StopPackageDialog({
	target,
	onClose,
}: {
	target: StopPackageTarget | null;
	onClose: () => void;
}) {
	const { t } = useTranslation("appointments", {
		keyPrefix: "stop_package_dialog",
	});
	const { locale, tzDate, timeFormatDateFns } = useDateContext();
	const [form, setForm] = useState<StopForm>(EMPTY);
	const [preview, setPreview] = useState<{
		key: string;
		data: StopPreview;
	} | null>(null);
	const [previewing, setPreviewing] = useState(false);
	const [stopping, setStopping] = useState(false);
	// `disabled={stopping}` engages only after a re-render, so a second click in the
	// same tick still posts a second stop, whose rejection then toasts over the success.
	const isSubmittingRef = useRef(false);
	const [showAllVisits, setShowAllVisits] = useState(false);
	// A rule's message shows once its field has been edited, or once Preview was pressed
	// with it broken: never on a form the admin has not touched yet.
	const [touched, setTouched] = useState<Partial<Record<keyof StopForm, true>>>(
		{},
	);
	const [attempted, setAttempted] = useState(false);
	// Drops a dry run that answers after the inputs it was asked about have changed.
	const requestKey = useRef("");

	const runPreview = async (next: StopForm, probeReason?: StopReason) => {
		if (!target) return;
		const key = keyOf(next);
		requestKey.current = key;
		setPreviewing(true);
		try {
			const res = await fetch(
				`/admin-portal/appointments/orders/${target.orderId}/stop-preview`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Accept: "application/json",
						"X-CSRF-Token": getCsrfToken(),
					},
					body: JSON.stringify({ form_data: payloadOf(next, probeReason) }),
				},
			);
			const body = await res.json();
			if (requestKey.current !== key) return;
			if (!res.ok || !body.success) {
				toast.error(body.error || t("errors.preview_failed"));
				return;
			}
			const data = body.data as StopPreview;
			if (next.billed.trim() === "") {
				// The opening dry run: fill in the backend's suggestion unless the admin has
				// typed an amount while it was in flight.
				const billed = String(data.suggestedBilledAmount);
				setForm((prev) => (prev.billed.trim() ? prev : { ...prev, billed }));
				setPreview({ key: keyOf({ ...next, billed }), data });
			} else {
				setPreview({ key, data });
			}
		} catch {
			if (requestKey.current === key) toast.error(t("errors.preview_failed"));
		} finally {
			if (requestKey.current === key) setPreviewing(false);
		}
	};

	// Opening asks for the summary straight away. stop_reason is required even on a dry
	// run, and does not move any number, so the opening one probes with PATIENT_REQUEST;
	// that preview never matches the form until the admin picks a reason themselves.
	// biome-ignore lint/correctness/useExhaustiveDependencies: runs once per opened order.
	useEffect(() => {
		setForm(EMPTY);
		setPreview(null);
		setPreviewing(false);
		setShowAllVisits(false);
		setTouched({});
		setAttempted(false);
		requestKey.current = "";
		if (target) runPreview(EMPTY, "PATIENT_REQUEST");
	}, [target]);

	const update = (patch: Partial<StopForm>) => {
		setForm((prev) => ({ ...prev, ...patch }));
		setTouched((prev) => ({
			...prev,
			...Object.fromEntries(Object.keys(patch).map((key) => [key, true])),
		}));
	};

	const error = target ? stopFormError(form, target.totalAmount) : null;
	const fresh = !!preview && preview.key === keyOf(form);
	const errorField = error ? STOP_FORM_ERROR_FIELD[error] : null;
	const fieldError = (field: keyof StopForm) =>
		error && errorField === field && (attempted || touched[field])
			? t(`errors.${error}`, { max: formatCurrency(target?.totalAmount) })
			: null;
	const reasonError = fieldError("reason");
	const notesError = fieldError("notes");
	const billedError = fieldError("billed");

	const requestPreview = () => {
		if (errorField) {
			setAttempted(true);
			document.getElementById(`stop-${errorField}`)?.focus();
			return;
		}
		runPreview(form);
	};
	const summary = preview?.data;
	const suggested = summary?.suggestedBilledAmount;
	const usingSuggestion =
		suggested != null &&
		form.billed.trim() !== "" &&
		Number(form.billed) === suggested;
	const discount = target?.discountAmount ?? 0;
	const pricePerVisit = target?.pricePerVisit ?? null;
	// The backend caps the suggestion at the order total, so price × visits can exceed it.
	const capped =
		!!summary &&
		suggested != null &&
		pricePerVisit != null &&
		pricePerVisit * summary.completedVisits > suggested;

	const confirm = () => {
		if (!target || error || !fresh || isSubmittingRef.current) return;
		isSubmittingRef.current = true;
		setStopping(true);
		router.post(
			`/admin-portal/appointments/orders/${target.orderId}/stop`,
			{ form_data: payloadOf(form) },
			{
				preserveScroll: true,
				preserveState: true,
				onStart: () => setStopping(true),
				onFinish: () => {
					isSubmittingRef.current = false;
					setStopping(false);
				},
				onSuccess: onClose,
			},
		);
	};

	const visitDate = (value: string | null) =>
		value
			? format(new Date(value), `dd MMM yyyy, ${timeFormatDateFns}`, {
					locale,
					in: tzDate,
				})
			: t("summary.unscheduled");

	return (
		<Dialog
			open={!!target}
			onOpenChange={(open) => {
				if (!open && !stopping) onClose();
			}}
		>
			<DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-[560px]">
				<DialogHeader>
					<DialogTitle>{t("title")}</DialogTitle>
					<DialogDescription>{t("description")}</DialogDescription>
				</DialogHeader>

				{target && (
					<dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 px-1">
						<div className="min-w-0">
							<LedgerLabel>{t("overview.registration_number")}</LedgerLabel>
							<dd className="font-mono text-sm font-semibold">
								{target.registrationNumber}
							</dd>
						</div>
						<div className="min-w-0">
							<LedgerLabel>{t("overview.patient")}</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{target.patientName || "—"}
							</dd>
						</div>
						<div className="min-w-0">
							<LedgerLabel>{t("overview.service")}</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{target.serviceName?.replaceAll("_", " ") || "—"}
							</dd>
						</div>
						<div className="min-w-0">
							<LedgerLabel>{t("overview.city")}</LedgerLabel>
							<dd className="text-sm font-medium leading-snug text-pretty">
								{target.city || "—"}
							</dd>
						</div>
					</dl>
				)}

				<div className="grid gap-3 px-0.5">
					<div className="grid gap-1.5">
						<Label htmlFor="stop-reason">{t("fields.reason")}</Label>
						<Select
							value={form.reason}
							onValueChange={(value) => update({ reason: value as StopReason })}
						>
							<SelectTrigger
								id="stop-reason"
								aria-invalid={!!reasonError}
								aria-describedby={reasonError ? "stop-reason-error" : undefined}
								className="shadow-inner bg-input"
							>
								<SelectValue placeholder={t("fields.reason_placeholder")} />
							</SelectTrigger>
							<SelectContent>
								{STOP_REASONS.map((reason) => (
									<SelectItem key={reason} value={reason}>
										{t(`reasons.${reason}`)}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FieldError id="stop-reason-error" message={reasonError} />
					</div>

					<div className="grid gap-1.5">
						<Label htmlFor="stop-notes">
							{t(
								form.reason === "OTHER"
									? "fields.notes_required"
									: "fields.notes",
							)}
						</Label>
						<Textarea
							id="stop-notes"
							value={form.notes}
							maxLength={NOTES_MAX}
							onChange={(e) => update({ notes: e.target.value })}
							aria-invalid={!!notesError}
							aria-describedby={notesError ? "stop-notes-error" : undefined}
							className="shadow-inner bg-input"
						/>
						<FieldError id="stop-notes-error" message={notesError} />
					</div>

					{summary && suggested != null && (
						<section
							aria-labelledby="stop-basis-title"
							className="grid gap-1 border bg-muted/40 p-3 text-sm"
						>
							<h3
								id="stop-basis-title"
								className="mb-0.5 text-xs font-medium text-muted-foreground"
							>
								{t("basis.title")}
							</h3>
							{target?.packageName && (
								<p className="mb-1 font-medium">
									{t("basis.package", {
										name: target.packageName,
										count: summary.packageVisits,
									})}
								</p>
							)}
							<SummaryRow
								ledger
								label={t("basis.completed")}
								value={t("summary.completed_value", {
									completed: summary.completedVisits,
									total: summary.packageVisits,
								})}
							/>
							<div className="grid grid-cols-[minmax(0,1fr)_auto] gap-2">
								<p className="text-muted-foreground">
									{t("summary.to_cancel")}
								</p>
								{summary.visitsToCancel.length ? (
									<div className="grid justify-items-end gap-0.5 font-medium tabular-nums">
										{(showAllVisits
											? summary.visitsToCancel
											: summary.visitsToCancel.slice(0, VISITS_SHOWN)
										).map((visit) => (
											<p key={visit.appointmentId}>
												#{visit.visitNumber} ·{" "}
												{visitDate(visit.appointmentDateTime)}
											</p>
										))}
										{summary.visitsToCancel.length > VISITS_SHOWN && (
											<button
												type="button"
												onClick={() => setShowAllVisits((all) => !all)}
												className="text-xs font-medium text-primary underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
											>
												{showAllVisits
													? t("basis.show_fewer")
													: t("basis.show_more", {
															count:
																summary.visitsToCancel.length - VISITS_SHOWN,
														})}
											</button>
										)}
									</div>
								) : (
									<p className="text-right font-medium">
										{t("summary.none_to_cancel")}
									</p>
								)}
							</div>
							{pricePerVisit != null && (
								<SummaryRow
									ledger
									label={t("basis.price_per_visit")}
									value={formatCurrency(pricePerVisit)}
									divider
								/>
							)}
							<SummaryRow
								ledger
								label={t("basis.completed_value")}
								value={formatCurrency(suggested)}
								emphasis
								divider={pricePerVisit == null}
							/>
							{capped && (
								<p className="text-xs text-muted-foreground">
									{t("basis.capped")}
								</p>
							)}
							{discount > 0 && (
								<>
									<SummaryRow
										ledger
										label={
											target?.voucherCode
												? t("basis.discount_code", { code: target.voucherCode })
												: t("basis.discount")
										}
										value={`−${formatCurrency(discount)}`}
									/>
									<p className="text-xs text-muted-foreground">
										{t("basis.discount_note")}
									</p>
								</>
							)}
							<SummaryRow
								ledger
								label={t("basis.order_total")}
								value={formatCurrency(summary.originalTotalAmount)}
								divider
							/>
							<SummaryRow
								ledger
								label={t("basis.paid")}
								value={formatCurrency(summary.settledAmount)}
							/>
						</section>
					)}

					<div className="grid gap-1.5">
						<Label htmlFor="stop-billed">{t("fields.billed")}</Label>
						<div className="relative">
							<span
								aria-hidden="true"
								className="pointer-events-none absolute inset-y-0 left-3 z-10 flex items-center text-sm text-muted-foreground"
							>
								Rp
							</span>
							<Input
								id="stop-billed"
								type="number"
								inputMode="numeric"
								min={0}
								max={target?.totalAmount}
								value={form.billed}
								onChange={(e) => update({ billed: e.target.value })}
								aria-invalid={!!billedError}
								aria-describedby={
									billedError
										? "stop-billed-error stop-billed-hint"
										: "stop-billed-hint"
								}
								className="shadow-inner bg-input pl-9 tabular-nums"
							/>
						</div>
						<FieldError id="stop-billed-error" message={billedError} />
						<div
							id="stop-billed-hint"
							className="grid gap-1 text-xs text-muted-foreground"
						>
							{suggested == null ? (
								<p>{t("fields.billed_calculating")}</p>
							) : usingSuggestion ? (
								<p>{t("fields.billed_hint_suggested")}</p>
							) : (
								<p className="flex flex-wrap items-center gap-x-1.5">
									{t("fields.billed_hint_edited", {
										suggested: formatCurrency(suggested),
									})}
									<button
										type="button"
										onClick={() => update({ billed: String(suggested) })}
										className="font-medium text-primary underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
									>
										{t("fields.billed_use_suggestion")}
									</button>
								</p>
							)}
						</div>
					</div>

					{summary && (
						<div
							className={cn(
								"grid gap-1 border p-3 text-sm transition-opacity",
								!fresh && "opacity-50",
							)}
						>
							<SummaryRow
								ledger
								label={t("summary.refund")}
								value={formatCurrency(summary.refundAmount)}
							/>
							<SummaryRow
								ledger
								label={t("summary.payment_status_after")}
								value={summary.paymentStatusAfter.replace(/_/g, " ")}
							/>
						</div>
					)}

					{summary && !fresh && !error && (
						<p className="text-xs text-muted-foreground">{t("stale")}</p>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" disabled={stopping} onClick={onClose}>
						{t("cancel")}
					</Button>
					<Button
						variant="outline"
						disabled={(fresh && !error) || previewing || stopping}
						onClick={requestPreview}
					>
						{previewing && <LoaderIcon className="animate-spin" />}
						{t("preview")}
					</Button>
					<Button
						variant="destructive"
						disabled={!!error || !fresh || previewing || stopping}
						onClick={confirm}
					>
						{stopping && <LoaderIcon className="animate-spin" />}
						{t("confirm")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

function SummaryRow({
	label,
	value,
	emphasis = false,
	divider = false,
	ledger = false,
}: {
	label: string;
	value: string;
	emphasis?: boolean;
	divider?: boolean;
	// A receipt line: amounts right-aligned so they stack into a column.
	ledger?: boolean;
}) {
	return (
		<div
			className={cn(
				"grid gap-2",
				ledger
					? "grid-cols-[minmax(0,1fr)_auto]"
					: "grid-cols-[minmax(0,2fr)_minmax(0,3fr)]",
				divider && "mt-0.5 border-t pt-1.5",
			)}
		>
			<p className={emphasis ? "font-medium" : "text-muted-foreground"}>
				{label}
			</p>
			<p
				className={cn(
					"whitespace-pre-line tabular-nums",
					emphasis ? "font-semibold" : "font-medium",
					ledger && "text-right",
				)}
			>
				{value}
			</p>
		</div>
	);
}

function FieldError({ id, message }: { id: string; message: string | null }) {
	if (!message) return null;
	return (
		<p id={id} role="alert" className="text-xs text-destructive">
			{message}
		</p>
	);
}
